import * as React from 'react'
import { MutableRefObject, useCallback, useMemo } from 'react'
import { CloseButtonProps, toast, ToastOptions } from 'react-toastify'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import { logger } from '@user/utils/logger'
import maplibregl from 'maplibre-gl'

type Props = {
  map: H.Map | undefined
  mlMap?: maplibregl.Map | null
  lastKnownPositionRef?: MutableRefObject<{ lat: number; lng: number } | null>
}

export function useLocateMe(props: Props) {
  const { map, mlMap, lastKnownPositionRef } = props
  const infoProps = useMemo<ToastOptions>(
    () => ({
      position: 'bottom-center',
      closeButton: (props: CloseButtonProps) => {
        return (
          <IconButton
            onClick={props.closeToast}
            data-test-id="closeBtn"
            sx={{ color: '#FFFFFF', padding: '4px' }}
          >
            <CloseIcon sx={{ marginLeft: '15px' }} />
          </IconButton>
        )
      },
      hideProgressBar: true,
      icon: false,
      style: {
        padding: '12px 16px',
        minWidth: '520px',
        backgroundColor: '#212121',
        color: '#FFFFFF',
        fontSize: '14px',
        minHeight: '40px',
        maxHeight: '40px',
        textAlign: 'center',
      },
    }),
    [],
  )

  const showLocationToast = useCallback(
    (message: string) => toast.info(message, infoProps),
    [infoProps],
  )

  const locateMe = useCallback(() => {
    // MapLibre path
    if (mlMap) {
      const flyTo = (latitude: number, longitude: number) => {
        const currentZoom = mlMap.getZoom()
        mlMap.flyTo({
          center: [longitude, latitude],
          zoom: currentZoom < 15 ? 16 : currentZoom,
        })
        localStorage.setItem(
          'mapCenter',
          JSON.stringify({ lat: latitude, lng: longitude }),
        )
      }

      // Reuse the position already tracked by watchPosition (instant, no new request).
      const cached = lastKnownPositionRef?.current
      if (cached) {
        flyTo(cached.lat, cached.lng)
        return
      }

      // No cached position yet — fall back to a one-shot request.
      if (!navigator.geolocation) {
        showLocationToast(
          'Location cannot be found due to poor service or technical issue.',
        )
        return
      }
      navigator.geolocation.getCurrentPosition(
        (position) =>
          flyTo(position.coords.latitude, position.coords.longitude),
        (error) => {
          if (error.code === error.PERMISSION_DENIED) {
            showLocationToast(
              'Location sharing permission is disabled. Your location cannot be found.',
            )
          } else {
            showLocationToast(
              'Location cannot be found due to poor service or technical issue.',
            )
          }
          logger.error('Geolocation Permission denied or unavailable')
        },
        { timeout: 10000, enableHighAccuracy: true, maximumAge: 30000 },
      )
      return
    }

    if (!map) return

    // Try to use existing current-location marker if present
    try {
      const objects = map.getObjects()
      const currentMarker = objects.find((obj: any) => {
        if (typeof obj.getData !== 'function') return false
        const data = obj.getData()
        return data?.contactLocation?.entityType === 'currentLocation'
      }) as any

      if (currentMarker && typeof currentMarker.getGeometry === 'function') {
        const geom = currentMarker.getGeometry() as { lat: number; lng: number }
        map.setCenter({ lat: geom.lat, lng: geom.lng }, false)
        const currentZoom = map.getZoom?.() as number | undefined
        if (currentZoom == null || currentZoom < 15) {
          map.setZoom?.(16, false)
        }
        localStorage.setItem(
          'mapCenter',
          JSON.stringify({ lat: geom.lat, lng: geom.lng }),
        )
        return
      }
    } catch (err) {
      logger.error('Error locating current location marker:', err)
    }

    if (!navigator.geolocation) {
      showLocationToast(
        'Location cannot be found due to poor service or technical issue.',
      )
      return
    }

    // Fallback to geolocation
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords
          map.setCenter({ lat: latitude, lng: longitude }, false)
          const currentZoom = map.getZoom?.() as number | undefined
          if (currentZoom == null || currentZoom < 15) {
            map.setZoom?.(16, false)
          }
          localStorage.setItem(
            'mapCenter',
            JSON.stringify({ lat: latitude, lng: longitude }),
          )
        },
        (error) => {
          if (error.code === error.PERMISSION_DENIED) {
            showLocationToast(
              'Location sharing permission is disabled. Your location cannot be found.',
            )
          } else {
            showLocationToast(
              'Location cannot be found due to poor service or technical issue.',
            )
          }
          logger.error('Geolocation Permission denied or unavailable')
        },
      )
    }
  }, [map, mlMap, lastKnownPositionRef, showLocationToast])

  return { locateMe }
}
